import { z } from 'zod';
import { parseISO, format } from 'date-fns';
import { PUT_API_MODULE_NAMES } from '@enroller/utils';
import { API_FIELDS_DEAL as DEAL, API_FIELDS_CONTACT as CONTACT } from '@enroller/admissions-components';

import {
  emailSchema,
  getPhotoSchema,
  internationalAddressDefaultValues,
  internationalAddressOptionalSchema,
  internationalAddressSchema,
  photoOptionalSchema,
} from '../../../constants/sharedSchemas';

import type { StudentApplicationValues } from '../StudentApplication';
import type { PageResponseData } from '../queries/graphqlResponse';

export const CURRENT_ADDRESS_FIELDS_NAME = 'currentAddressFields';

export enum PAGE_SECTIONS {
  personalInformation = 'personalInformation',
  contactInformation = 'contactInformation',
  yourPhoto = 'yourPhoto',
}

export const studentDetailsSchema = {
  required: z.object({
    [PAGE_SECTIONS.personalInformation]: z.object({
      [CONTACT.firstName]: z.string().min(1, 'First Name is required'),
      [CONTACT.lastName]: z.string().min(1, 'Last Name is required'),
      [CONTACT.preferredName]: z.string().optional(),
      [DEAL.programType]: z.string().min(1, 'Program is required'),
      [CONTACT.gender]: z.string().min(1, 'Gender is required'),
      [CONTACT.dateOfBirth]: z.date({ required_error: 'Date of birth is required' }),
      [CONTACT.cityOfBirth]: z.string().min(1, 'City of birth is required'),
      [CONTACT.countryOfBirth]: z.string().min(1, 'Country of birth is required'),
      [CONTACT.residence]: z.string().min(1, 'Country of legal residence is required'),
      [CONTACT.citizenship]: z.string().min(1, 'Country of citizenship is required'),
    }),
    [PAGE_SECTIONS.contactInformation]: z.object({
      [CONTACT.email]: emailSchema,
      [CONTACT.mobile]: z.string().min(1, 'Cell phone number is required'),
      [CONTACT.homePhone]: z.string().min(1, 'Home phone number is required'),
      [CURRENT_ADDRESS_FIELDS_NAME]: internationalAddressSchema,
      [CONTACT.emergencyContactName]: z.string().min(1, 'Emergency contact name is required'),
      [CONTACT.emergencyContactRelation]: z.string().min(1, 'Emergency contact relation is required'),
      [CONTACT.emergencyContactPhoneNumber]: z.string().min(1, 'Emergency contact phone number is required'),
    }),
    [PAGE_SECTIONS.yourPhoto]: z.object({ applicantPhoto: getPhotoSchema('Your photo') }),
  }),

  optional: z.object({
    [PAGE_SECTIONS.personalInformation]: z.object({
      [CONTACT.firstName]: z.string().optional(),
      [CONTACT.lastName]: z.string().optional(),
      [CONTACT.preferredName]: z.string().optional(),
      [DEAL.programType]: z.string().optional(),
      [CONTACT.gender]: z.string().optional(),
      [CONTACT.dateOfBirth]: z.date().optional(),
      [CONTACT.cityOfBirth]: z.string().optional(),
      [CONTACT.countryOfBirth]: z.string().optional(),
      [CONTACT.residence]: z.string().optional(),
      [CONTACT.citizenship]: z.string().optional(),
    }),
    [PAGE_SECTIONS.contactInformation]: z.object({
      [CONTACT.email]: z.string().email('Email is invalid').optional(),
      [CONTACT.mobile]: z.string().optional(),
      [CONTACT.homePhone]: z.string().optional(),
      [CURRENT_ADDRESS_FIELDS_NAME]: internationalAddressOptionalSchema,
      [CONTACT.emergencyContactName]: z.string().optional(),
      [CONTACT.emergencyContactRelation]: z.string().optional(),
      [CONTACT.emergencyContactPhoneNumber]: z.string().optional(),
    }),
    [PAGE_SECTIONS.yourPhoto]: z.object({ applicantPhoto: photoOptionalSchema }),
  }),
};

// Infer type of form values from schema
export type StudentDetailsFormValues = z.infer<(typeof studentDetailsSchema)['required']> | z.infer<(typeof studentDetailsSchema)['optional']>;

export const defaultValues: StudentDetailsFormValues = {
  [PAGE_SECTIONS.personalInformation]: {
    // personal information
    [CONTACT.firstName]: '',
    [CONTACT.lastName]: '',
    [CONTACT.preferredName]: '',
    [DEAL.programType]: '',
    [CONTACT.gender]: '',
    [CONTACT.dateOfBirth]: undefined, // Date

    // location information
    [CONTACT.cityOfBirth]: '',
    [CONTACT.countryOfBirth]: '',
    [CONTACT.residence]: '',
    [CONTACT.citizenship]: '',
  },
  [PAGE_SECTIONS.contactInformation]: {
    // base contacts
    [CONTACT.email]: undefined,
    [CONTACT.mobile]: '',
    [CONTACT.homePhone]: '',

    // current address with auto-complete fields
    [CURRENT_ADDRESS_FIELDS_NAME]: internationalAddressDefaultValues,

    // emergency contact
    [CONTACT.emergencyContactName]: '',
    [CONTACT.emergencyContactRelation]: '',
    [CONTACT.emergencyContactPhoneNumber]: '',
  },
  // Profile photo
  // TODO: change once upload endpoints are implemented
  [PAGE_SECTIONS.yourPhoto]: { applicantPhoto: { id: '' } },
};

export const createStudentDetailsPayload = (values: StudentDetailsFormValues, modulesID: StudentApplicationValues['modulesID']) => {
  const { [DEAL.programType]: programTypeSelected, [CONTACT.dateOfBirth]: dateOfBirth, ...personalInfo } = values[PAGE_SECTIONS.personalInformation];
  const { [CURRENT_ADDRESS_FIELDS_NAME]: currentAddress, ...contactFields } = values[PAGE_SECTIONS.contactInformation];

  return {
    data: {
      [PUT_API_MODULE_NAMES.CONTACTS]: [
        {
          [CONTACT.ID]: modulesID[PUT_API_MODULE_NAMES.CONTACTS],
          [CONTACT.dateOfBirth]: dateOfBirth ? format(dateOfBirth, 'yyyy-MM-dd') : '',
          ...contactFields,
          ...currentAddress,
          ...personalInfo,
        },
      ],
      [PUT_API_MODULE_NAMES.DEALS]: [
        {
          [DEAL.ID]: modulesID[PUT_API_MODULE_NAMES.DEALS],
          [DEAL.programType]: programTypeSelected,
        },
      ],
    },
  };
};

export const createStudentDetailsResponse = (studentDetailsResponse: PageResponseData['studentDetails']): StudentDetailsFormValues => {
  const contact = studentDetailsResponse?.Student;
  const deal = studentDetailsResponse?.Deal;

  if (!contact || !deal) {
    console.warn('Error creating student details response: studentDetailsResponse is undefined');

    return defaultValues;
  }

  try {
    const dateOfBirth = contact[CONTACT.dateOfBirth]?.value;

    return {
      [PAGE_SECTIONS.personalInformation]: {
        [CONTACT.firstName]: contact[CONTACT.firstName]?.value || '',
        [CONTACT.lastName]: contact[CONTACT.lastName]?.value || '',
        [CONTACT.preferredName]: contact[CONTACT.preferredName]?.value || '',
        [CONTACT.gender]: contact[CONTACT.gender]?.value || '',
        [CONTACT.dateOfBirth]: dateOfBirth ? parseISO(dateOfBirth) : undefined,
        [CONTACT.cityOfBirth]: contact[CONTACT.cityOfBirth]?.value || '',
        [CONTACT.countryOfBirth]: contact[CONTACT.countryOfBirth]?.value || '',
        [CONTACT.residence]: contact[CONTACT.residence]?.value || '',
        [CONTACT.citizenship]: contact[CONTACT.citizenship]?.value || '',
        [DEAL.programType]: deal[DEAL.programType]?.value || '',
      },
      [PAGE_SECTIONS.contactInformation]: {
        [CONTACT.email]: contact[CONTACT.email]?.value || '',
        [CONTACT.mobile]: contact[CONTACT.mobile]?.value || '',
        [CONTACT.homePhone]: contact[CONTACT.homePhone]?.value || '',
        [CURRENT_ADDRESS_FIELDS_NAME]: {
          [CONTACT.addressLine1]: contact[CONTACT.addressLine1]?.value || '',
          [CONTACT.addressLine2]: contact[CONTACT.addressLine2]?.value || '',
          [CONTACT.city]: contact[CONTACT.city]?.value || '',
          [CONTACT.postalCode]: contact[CONTACT.postalCode]?.value || '',
          [CONTACT.country]: contact[CONTACT.country]?.value || '',
        },
        [CONTACT.emergencyContactName]: contact[CONTACT.emergencyContactName]?.value || '',
        [CONTACT.emergencyContactRelation]: contact[CONTACT.emergencyContactRelation]?.value || '',
        [CONTACT.emergencyContactPhoneNumber]: contact[CONTACT.emergencyContactPhoneNumber]?.value || '',
      },
      [PAGE_SECTIONS.yourPhoto]: {
        applicantPhoto: { id: contact[CONTACT.ID]?.value || '' },
      },
    };
  } catch (e) {
    console.error('Error creating student details response:', e);

    return defaultValues;
  }
};
